// src/sanitizeHtml.js
// Текст поста выводится через dangerouslySetInnerHTML, поэтому перед показом
// он проходит через белый список тегов и атрибутов. Постами управляют только
// администраторы, но одна вставка из Word с чужим <script> не должна
// превращаться в исполняемый код на странице.

const ALLOWED_TAGS = new Set([
  'B', 'STRONG', 'I', 'EM', 'U', 'S', 'STRIKE', 'DEL', 'MARK',
  'P', 'BR', 'DIV', 'SPAN',
  'UL', 'OL', 'LI',
  'H1', 'H2', 'H3', 'H4', 'H5', 'H6',
  'BLOCKQUOTE', 'CODE', 'PRE',
  'A',
]);

// Разрешаем только безопасные атрибуты; style пропускаем отдельным фильтром
const ALLOWED_ATTRS = {
  A: ['href', 'target', 'rel', 'class'],
  SPAN: ['class'],
  MARK: ['class'],
  DIV: ['class'],
  P: ['class'],
};

// Из инлайновых стилей оставляем только начертание.
// Цвета текста и фона не пропускаем: вставка из Word приносит чёрный текст
// и белую заливку, которые в тёмной теме становятся нечитаемыми.
// Выделение маркером хранится тегом <mark> и красится темой.
const ALLOWED_STYLES = new Set([
  'font-weight', 'font-style', 'text-decoration', 'text-decoration-line',
]);

const SAFE_URL = /^(https?:|mailto:|tel:|\/|#)/i;

function cleanStyle(value) {
  return String(value)
    .split(';')
    .map((chunk) => chunk.trim())
    .filter(Boolean)
    .filter((chunk) => {
      const name = chunk.split(':')[0].trim().toLowerCase();
      if (!ALLOWED_STYLES.has(name)) return false;
      // url(...) и expression(...) в значениях недопустимы
      return !/url\s*\(|expression\s*\(/i.test(chunk);
    })
    .join('; ');
}

function cleanNode(node) {
  const children = Array.from(node.childNodes);

  children.forEach((child) => {
    if (child.nodeType === Node.TEXT_NODE) return;

    if (child.nodeType !== Node.ELEMENT_NODE) {
      child.remove();
      return;
    }

    if (!ALLOWED_TAGS.has(child.tagName)) {
      // Тег запрещён, но его текст сохраняем — кроме скриптов и стилей
      if (child.tagName === 'SCRIPT' || child.tagName === 'STYLE') {
        child.remove();
      } else {
        cleanNode(child);
        child.replaceWith(...Array.from(child.childNodes));
      }
      return;
    }

    const allowed = ALLOWED_ATTRS[child.tagName] || [];
    Array.from(child.attributes).forEach((attr) => {
      const name = attr.name.toLowerCase();
      if (name === 'style') {
        const cleaned = cleanStyle(attr.value);
        if (cleaned) child.setAttribute('style', cleaned);
        else child.removeAttribute('style');
        return;
      }
      if (!allowed.includes(name)) {
        child.removeAttribute(attr.name);
      }
    });

    if (child.tagName === 'A') {
      const href = child.getAttribute('href') || '';
      if (!SAFE_URL.test(href.trim())) {
        child.removeAttribute('href');
      } else {
        child.setAttribute('target', '_blank');
        child.setAttribute('rel', 'noopener noreferrer');
      }
    }

    cleanNode(child);
  });
}

export default function sanitizeHtml(html) {
  if (!html) return '';
  // template не выполняет скрипты и не загружает картинки при разборе
  const template = document.createElement('template');
  template.innerHTML = String(html);
  cleanNode(template.content);
  return template.innerHTML;
}
